import React from 'react';
import { Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';

interface BookReviewFiltersProps {
  filterRating: number | null;
  onFilterChange: (rating: number | null) => void;
  sortBy: 'recent' | 'popular';
  onSortChange: (sort: 'recent' | 'popular') => void;
  totalReviews: number;
  filteredCount: number;
  ratingDistribution?: { [key: number]: number };
}

export function BookReviewFilters({
  filterRating,
  onFilterChange,
  sortBy,
  onSortChange,
  totalReviews,
  filteredCount,
  ratingDistribution = {}
}: BookReviewFiltersProps) {
  const ratingOptions = [
    { value: null, label: 'All' },
    { value: 5, label: '5★' },
    { value: 4, label: '4★' },
    { value: 3, label: '3★' },
    { value: 2, label: '2★' },
    { value: 1, label: '1★' }
  ];

  return (
    <div className="space-y-4">
      {/* Star Rating Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-muted-foreground">Filter by rating:</span>
        <div className="flex flex-wrap gap-2">
          {ratingOptions.map((option) => {
            const count = option.value ? ratingDistribution[option.value] || 0 : totalReviews;
            const isActive = filterRating === option.value;
            
            return (
              <Button
                key={option.value ?? 'all'}
                variant={isActive ? 'default' : 'outline'}
                size="sm"
                onClick={() => onFilterChange(option.value)}
                className={cn(
                  'gap-1',
                  isActive && 'bg-primary text-primary-foreground'
                )}
              >
                {option.value && <Star className="w-3 h-3 fill-current" />}
                {option.label}
                <span className="text-xs opacity-70">({count})</span>
              </Button>
            );
          })}
        </div>
      </div>

      {/* Sort Options */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Sort by:</span>
          <Select value={sortBy} onValueChange={(value) => onSortChange(value as 'recent' | 'popular')}>
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="recent">Most Recent</SelectItem>
              <SelectItem value="popular">Most Popular</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Result count */}
        {filteredCount !== totalReviews && (
          <span className="text-sm text-muted-foreground">
            Showing {filteredCount} of {totalReviews} reviews
          </span>
        )}
      </div>
    </div>
  );
}
